import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import { logger } from '@marriott/mi-headless-utils';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';

import { getRetargetingPropertyImageQuery } from '../queries';
import {
  DEFAULT_HOMEPAGE_DAC_IMAGE_DOMAIN,
  RETARGETING_UXL_PARTIAL_ERROR_EVENT,
  RETARGETING_UXL_REQUEST_ERROR_EVENT,
} from './retargeting.constants';

const { log } = logger({})('useGetRetargetingPropertyImage');

type PropertyImageResponse = {
  property?: {
    media?: {
      primaryImage?: {
        edges?: Array<{
          node?: {
            imageUrls?: {
              square?: string | null;
              wideHorizontal?: string | null;
            } | null;
          } | null;
        } | null> | null;
      } | null;
    } | null;
  } | null;
};

function resolveImageUrl(value: unknown, imageDomain: string): string | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  const url = value.trim();
  if (/^(?:https?:)?\/\//i.test(url)) return url;
  return `${imageDomain.replace(/\/+$/, '')}${url.startsWith('/') ? url : `/${url}`}`;
}

export function mapRetargetingPropertyImage(
  data: PropertyImageResponse | null | undefined,
  imageDomain = DEFAULT_HOMEPAGE_DAC_IMAGE_DOMAIN
): string | null {
  const edges = data?.property?.media?.primaryImage?.edges ?? [];
  for (const edge of edges) {
    for (const rendition of ['square', 'wideHorizontal'] as const) {
      const resolved = resolveImageUrl(edge?.node?.imageUrls?.[rendition], imageDomain);
      if (resolved) return resolved;
    }
  }
  return null;
}

export function useGetRetargetingPropertyImage(): (marshaCode: string) => Promise<string | null> {
  const client = useApolloClient();
  const requestId = useClientEnvVarsStore(state => state.envVarsObject?.['REQUEST_ID']);
  const configuredHomepageImageDomain = useClientEnvVarsStore(state => state.envVarsObject?.['UXL_IMAGE_CACHE_DOMAIN']);
  const configuredCompatibilityImageDomain = useClientEnvVarsStore(state => state.envVarsObject?.['DAC_IMG_DOMAIN']);
  const imageDomain =
    (typeof configuredHomepageImageDomain === 'string' && configuredHomepageImageDomain.trim()) ||
    (typeof configuredCompatibilityImageDomain === 'string' && configuredCompatibilityImageDomain.trim()) ||
    DEFAULT_HOMEPAGE_DAC_IMAGE_DOMAIN;

  return useCallback(
    async (marshaCode: string) => {
      const propertyId = marshaCode.trim().toUpperCase();
      if (!propertyId) return null;

      try {
        const result = await client.query<PropertyImageResponse, { propertyId: string }>({
          query: getRetargetingPropertyImageQuery,
          variables: { propertyId },
          fetchPolicy: 'cache-first',
          errorPolicy: 'all',
          context: {
            headers: {
              ...(requestId && { 'x-request-id': requestId }),
            },
          },
        });

        const imageUrl = mapRetargetingPropertyImage(result.data, imageDomain);
        if (result.errors?.length || result.error) {
          log.error(RETARGETING_UXL_PARTIAL_ERROR_EVENT, {
            hasUsableImage: Boolean(imageUrl),
          });
        }

        return imageUrl;
      } catch (error) {
        log.error(RETARGETING_UXL_REQUEST_ERROR_EVENT, {
          errorType: error instanceof Error ? error.name : typeof error,
        });
        return null;
      }
    },
    [client, imageDomain, requestId]
  );
}
